import { FC, useState } from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { CustomerGetResponse, CustomerUser } from 'src/interfaces/user/customer';
import { phoneRegExp } from 'src/components/common/common-regex';
import { userApi } from 'src/api/user';
import { InputField } from '../../../input-field';

interface AddCustomerProfileProps {
  open: boolean;
  onClose: () => void;
  mobileNumber: string;
  customerDetails?: CustomerGetResponse | null;
  addEditCustomerProfile: (data: CustomerUser, isAdd: boolean) => Promise<boolean>;
}

export const AddCustomerProfile: FC<AddCustomerProfileProps> = (props) => {
  const { open, onClose, mobileNumber, addEditCustomerProfile, customerDetails } = props;
  const [mobileDuplicate, setMobileDuplicate] = useState<boolean>(false);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      firstName: customerDetails?.firstName || '',
      lastName: customerDetails?.lastName || '',
      email: customerDetails?.email || '',
      mobile: customerDetails?.mobileNumber
        ? customerDetails?.mobileNumber.slice(3) : mobileNumber.slice(3),
      alternateMobile: customerDetails?.altMobileNumber || '',
      pincode: customerDetails?.pincode ? customerDetails?.pincode.toString() : '',
      submit: null
    },
    validationSchema: Yup.object().shape({
      firstName: Yup.string().matches(/^[A-Za-z0-9][A-Za-z0-9\s]*$/, 'Invalid first name').required('Required'),
      lastName: Yup.string().matches(/^[A-Za-z0-9][A-Za-z0-9\s]*$/, 'Invalid last name').required('Required'),
      email: Yup.string().email('Invalid email').max(255).optional(),
      mobile: Yup.string().matches(phoneRegExp, 'Mobile Number is not valid').required('Mobile Number is Required'),
      alternateMobile: Yup.string().matches(phoneRegExp, 'Mobile Number is not valid').optional(),
      pincode: Yup.string().matches(new RegExp(/^[1-9]{1}[0-9]{5}$/),
        'Invalid Pincode').required('Required'),
    }),

    onSubmit: async (values) => {
      const profileDetails : CustomerUser = {
        firstName: values.firstName,
        lastName: values.lastName,
        altMobileNumber: values.alternateMobile,
        email: values.email,
        pincode: parseInt(values.pincode, 10),
      };
      if (customerDetails?.firstName) {
        profileDetails.username = customerDetails?.username;
      } else {
        profileDetails.mobileNumber = `+91${values.mobile}`;
      }
      const result = addEditCustomerProfile(profileDetails, !customerDetails?.firstName);
      if (result) {
        formik.resetForm();
        onClose();
      }
    }
  });

  // this method maintain the original values when we close the dialog
  const closeDialog = (e, reason) => {
    if (reason !== 'backdropClick') {
      formik.resetForm();
      setMobileDuplicate(false);
      onClose();
    }
  };

  const handleMobileExist = async (value) => {
    const enteredMobile = value?.trim();
    if (phoneRegExp.test(enteredMobile)) {
      const responseOfMobile = await userApi
        .isCustomerExists(`+91${enteredMobile}`);
      if (responseOfMobile?.responseData) {
        setMobileDuplicate(true);
        await formik.setFieldError('mobile', 'This Mobile Number is already in use');
      } else {
        await formik.setFieldError('mobile', undefined);
        setMobileDuplicate(false);
      }
    }
    await formik.setFieldTouched('mobile', true);
  };

  const mobileNumberChangeHandler = (event) => {
    formik.setFieldValue('mobile', event?.target?.value);
    if (event?.target?.value?.trim()?.length === 10) {
      handleMobileExist(event?.target?.value?.trim());
    }
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      onClose={closeDialog}
      open={open}
    >
      <DialogTitle>
        {customerDetails !== null ? 'Edit Customer Profile' : 'Add Customer Profile'}
        <IconButton
          sx={{
            position: 'absolute',
            right: 8,
            top: 8
          }}
          color="inherit"
          onClick={(e) => closeDialog(e, '')}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Grid
          container
          spacing={2}
        >
          <Grid
            item
            xs={12}
            md={6}
          >
            <InputField
              error={Boolean(formik.touched.firstName && formik.errors.firstName)}
              helperText={formik.touched.firstName && formik.errors.firstName}
              fullWidth
              required
              label="First Name"
              name="firstName"
              onChange={(event) => {
                if (event.target.value !== ' ') {
                  formik.setFieldValue('firstName', event.target.value);
                }
              }}
              onBlur={formik.handleBlur}
              placeholder="Enter First Name"
              value={formik.values.firstName}
            />
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
          >
            <InputField
              error={Boolean(formik.touched.lastName && formik.errors.lastName)}
              helperText={formik.touched.lastName && formik.errors.lastName}
              fullWidth
              required
              label="Last Name"
              name="lastName"
              onChange={(event) => {
                if (event.target.value !== ' ') {
                  formik.setFieldValue('lastName', event.target.value);
                }
              }}
              onBlur={formik.handleBlur}
              placeholder="Enter Last Name"
              value={formik.values.lastName}
            />
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
          >
            <InputField
              error={Boolean(formik.touched.mobile
                && formik.errors.mobile)}
              helperText={formik.touched.mobile
                && formik.errors.mobile}
              fullWidth
              required
              disabled={customerDetails !== null}
              label="Mobile Number"
              name="mobile"
              onChange={mobileNumberChangeHandler}
              placeholder="Mobile Number"
              value={formik.values.mobile}
              InputProps={{
                startAdornment: (
                  <InputAdornment
                    position="start"
                  >
                    +91
                  </InputAdornment>
                ),
              }}
            />
            { mobileDuplicate && !formik.errors.mobile && (
            <Grid
              sx={{
                ml: '14px',
              }}
            >
              <Typography
                lineHeight={0.5}
                sx={{
                  fontWeight: '1'
                }}
                variant="caption"
                color="red"
              >
                This Mobile Number is already in use
              </Typography>
            </Grid>
            )}
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
          >
            <InputField
              error={Boolean(formik.touched.alternateMobile
                && formik.errors.alternateMobile)}
              helperText={formik.touched.alternateMobile
                && formik.errors.alternateMobile}
              fullWidth
              label="Alternate Mobile Number"
              name="alternateMobile"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Alternate Mobile Number"
              value={formik.values.alternateMobile}
              InputProps={{
                startAdornment: (
                  <InputAdornment
                    position="start"
                  >
                    +91
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
          >
            <InputField
              error={Boolean(formik.touched.pincode && formik.errors.pincode)}
              helperText={formik.touched.pincode && formik.errors.pincode}
              fullWidth
              required
              label="Pincode"
              name="pincode"
              onChange={(event) => {
                if (event.target.value !== ' ') {
                  formik.setFieldValue('pincode', event.target.value);
                }
              }}
              type="number"
              onBlur={formik.handleBlur}
              placeholder="Pincode"
              value={formik.values.pincode}
            />
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
          >
            <InputField
              error={Boolean(formik.touched.email && formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
              fullWidth
              label="Email"
              name="email"
              onChange={(event) => {
                if (event.target.value !== ' ') {
                  formik.setFieldValue('email', event.target.value);
                }
              }}
              onBlur={formik.handleBlur}
              placeholder="Email"
              value={formik.values.email}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions
        sx={{
          alignItems: 'right'
        }}
      >
        <Button
          color="primary"
          onClick={(e) => closeDialog(e, '')}
          variant="text"
        >
          Cancel
        </Button>
        <Button
          color="primary"
          disabled={formik.isSubmitting || !(formik.isValid && formik.dirty)}
          onClick={() => { formik.handleSubmit(); }}
          variant="contained"
        >
          {customerDetails !== null ? 'Update' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

AddCustomerProfile.defaultProps = {
  open: false
};

AddCustomerProfile.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  addEditCustomerProfile: PropTypes.func,
  mobileNumber: PropTypes.string,
  // @ts-ignore
  customerDetails: PropTypes.object
};
